import { useEffect } from "react";
import {
  Drawer,
  SwipeableDrawer,
  Stack,
  Typography,
  Toolbar,
  Box
} from "@mui/material";
import { useIsDesktopLayout } from "../../../hooks/useIsDesktopLayout";
import Workspaces from "./side-navigation-items/workspace/Workspaces";

export interface SideNavigationProps{
  open: boolean,
  width?: number,
  onChange: (open: boolean) => void
}

const SideNavigation = ({width = 250, open, onChange}: SideNavigationProps) => {

  const isMediumScreen = useIsDesktopLayout();
  // Sync drawer open-state to the breakpoint only when it actually flips;
  // onChange is a caller-owned prop and must not retrigger this effect.
  useEffect(() => {
    if(isMediumScreen) onChange(true)
    else onChange(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMediumScreen]);

  // Shared paper styling so both desktop (persistent Drawer) and mobile
  // (SwipeableDrawer) render the panel identically.
  const paperSx = {
    [`& .MuiDrawer-paper`]: {
      width: width,
      boxSizing: 'border-box' as const,
      height: "100%",
      borderRadius: 0,
      borderRight: 'none',
    },
  };

  const drawerContent = (
    <Stack maxHeight="100%" height='100%'>
      <Toolbar />

      <Box flex={1} overflow='auto' pt={2}>
        <Workspaces />
      </Box>

      <Stack px={2} py={1}>
        <Typography variant="caption">Powered By:</Typography>
        <Typography variant="caption">Thynker Technologies Inc.</Typography>
      </Stack>

    </Stack>
  );

  // On mobile we use SwipeableDrawer instead of `Drawer variant="temporary"`.
  // The plain temporary Drawer suffered from a phantom-backdrop-close on
  // iOS / touch viewports: tapping the hamburger opened the drawer, but the
  // SAME touch event's touchend landed on the drawer's freshly-mounted
  // backdrop and immediately fired `onClose`, snapping it shut within
  // ~100ms. Users perceived this as "needs 2 taps" (UAT row 18). The
  // first tap opens-then-closes, the second tap opens for real because
  // the touch starts after the backdrop is already settled.
  // SwipeableDrawer is the MUI-recommended component for mobile and handles
  // the touch/click sequencing correctly. `keepMounted` keeps the DOM
  // around so the first open animation also doesn't have to mount fresh.
  if (!isMediumScreen) {
    return (
      <SwipeableDrawer
        anchor="left"
        open={open}
        onOpen={() => onChange(true)}
        onClose={() => onChange(false)}
        disableBackdropTransition
        disableDiscovery
        ModalProps={{ keepMounted: true }}
        sx={{ ...paperSx, pointerEvents: open ? "auto" : "none" }}
      >
        {drawerContent}
      </SwipeableDrawer>
    );
  }

  return(
    <Drawer
      open={open}
      variant="persistent"
      sx={paperSx}
      onClose={() => onChange(false)}
    >
      {drawerContent}
    </Drawer>
  )
};


export default SideNavigation;
